import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setError } from "../../alerts/alertsSlice";
import {
  useGetAnalysisQuery,
  usePostAnalysesMutation,
  usePatchAnalysisFeaturesMutation,
  usePatchAnalysisConclusionsMutation,
} from "../analysesSlice";
import useSearchParams from "../../../hooks/useSearchParams";

const useAnalysisData = ({ analysisId, setAnalysisId }) => {
  const [mutationLoading, setMutationLoading] = useState(false);
  const [analysisMutation] = usePostAnalysesMutation();
  const [analysisFeaturesMutation] = usePatchAnalysisFeaturesMutation();
  const [analysisConclusionsMutation] = usePatchAnalysisConclusionsMutation();
  const { assessmentId } = useParams();
  const dispatch = useDispatch();
  const { getParam } = useSearchParams();
  const profileId = getParam("profile_id");

  const analysis = useGetAnalysisQuery(
    { id: analysisId },
    { skip: analysisId == null }
  );

  const featuresStatus = useMemo(() => analysis?.status, [analysis?.status]);
  const conclusionsStatus = useMemo(() => analysis?.status, [analysis?.status]);
  const featuresData = useMemo(
    () => analysis?.data?.features ?? [],
    [analysis?.data?.features]
  );
  const conclusionsData = useMemo(
    () => analysis?.data?.conclusions ?? [],
    [analysis?.data?.conclusions]
  );
  const analysisState = useMemo(
    () => analysis?.data?.state ?? [],
    [analysis?.data?.state]
  );

  const createAnalysis = async () => {
    let result;
    try {
      setMutationLoading(true);
      result = await analysisMutation({
        body: {
          assessment_id: assessmentId,
          analysis_profile_id: profileId,
        },
      });
      if (result?.data?.analysis_id) {
        setAnalysisId(result?.data?.analysis_id);
      }
    } catch (err) {
      dispatch(setError(err));
      console.error(err);
    } finally {
      setMutationLoading(false);
    }
  };

  const patchAnalysisFeatures = async (selectedFeatures) => {
    try {
      setMutationLoading(true);
      await analysisFeaturesMutation({
        params: { analysisId },
        body: {
          feature_ids: selectedFeatures?.value,
        },
      });
    } catch (err) {
      dispatch(setError(err));
      console.error(err);
    } finally {
      setMutationLoading(false);
    }
  };

  const patchAnalysisConclusions = async (
    selectedConclusionOptions,
    advance = false
  ) => {
    try {
      setMutationLoading(true);
      await analysisConclusionsMutation({
        params: { analysisId },
        body: {
          conclusion_ids: selectedConclusionOptions?.value,
          advance: advance,
        },
      });
    } catch (err) {
      dispatch(setError(err));
      console.error(err);
    } finally {
      setMutationLoading(false);
    }
  };

  useEffect(() => {
    if (analysis.isError && analysis.error != null) {
      dispatch(setError(analysis?.error));
    }
    // eslint-disable-next-line
  }, [analysis.isError]);

  useEffect(() => {
    if (analysis?.data?.state !== "INFERENCE") return;
    if (typeof analysis?.refetch !== "function") return;
    const interval = setInterval(() => {
      analysis?.refetch?.();
    }, 2000);
    return () => clearInterval(interval); // cleanup
    // eslint-disable-next-line
  }, [analysis?.data?.state]);

  const isLoading = (status) =>
    status !== "fulfilled" &&
    status !== "rejected" &&
    status !== "uninitialized";

  return {
    analysisLoading: isLoading(analysis?.status) || mutationLoading,
    analysis,
    analysisState,
    createAnalysis,
    patchAnalysisFeatures,
    patchAnalysisConclusions,
    featuresStatus,
    conclusionsStatus,
    featuresData,
    conclusionsData,
  };
};

export default useAnalysisData;
